import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { PageHeader } from "../components/ui/PageHeader";
import { SettingsModal } from "../components/ui/SettingsModal";
import { ScanHistoryPanel } from "../components/manager/ScanHistoryPanel";
import { ValidSetsPanel } from "../components/manager/ValidSetsPanel";
import { InvalidSetsPanel } from "../components/manager/InvalidSetsPanel";
import { ActivityReportsPanel } from "../components/manager/ActivityReportsPanel";
import ReportService from "../services/ReportService";
import apiService from "../services/ApiService";
import { ShiftsPanel } from "../components/manager/ShiftsPanel";
import { MembersPanel } from "../components/manager/MembersPanel";

type TabType = "scan-history" | "reports-operators" | "shifts" | "members";

export const ManagerView: React.FC = () => {
  const navigate = useNavigate();
  const {
    scanHistory,
    operators,
    setIsManagerAuthenticated,
    cameraConfig,
    setCameraConfig,
  } = useAppContext();

  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("scan-history");

  // Get available dates from scan history
  const availableDates = useMemo(
    () => Object.keys(scanHistory).sort(),
    [scanHistory]
  );

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedOperator, setSelectedOperator] = useState<string>("");

  const handleExit = () => {
    // Logout from API if authenticated
    if (apiService.isAuthenticated()) {
      // Clear tokens for manager role specifically
      apiService.logoutForRole("manager");
      console.log("Logged out manager from API");
    }

    setIsManagerAuthenticated(false);
    navigate("/");
  };

  const handleGenerateReport = (
    reportType: string,
    params?: { endDate?: string; startDate?: string; date?: string }
  ) => {
    switch (reportType) {
      case "All Scans Report":
        ReportService.generateAllScansReport(scanHistory);
        break;
      case "Exact Date Scans Report":
        if (params?.date) {
          ReportService.generateExactDateReport(scanHistory, {
            date: params.date,
          });
        }
        break;
      case "Date Range Scans Report":
        if (params?.startDate && params?.endDate) {
          ReportService.generateDateRangeReport(scanHistory, {
            startDate: params.startDate,
            endDate: params.endDate,
          });
        }
        break;
      case "Daily Summary Report":
        ReportService.generateDailySummary(scanHistory);
        break;
      case "Weekly Summary Report":
        ReportService.generateWeeklySummary(scanHistory);
        break;
      case "Monthly Summary Report":
        ReportService.generateMonthlySummary(scanHistory);
        break;
      default:
        console.warn(`Unknown report type: ${reportType}`);
    }
  };

  const tabButtonClass = (tabKey: TabType) =>
    `px-6 py-3 font-medium text-sm rounded-t-lg transition-colors ${
      activeTab === tabKey
        ? "bg-white text-blue-600 border-b-2 border-blue-600"
        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
    }`;

  return (
    <div className="h-screen w-screen overflow-hidden p-4 bg-gray-50">
      <PageHeader
        title={
          <div className="flex items-center gap-3">
            <span>LumiBot - Manager View</span>
          </div>
        }
        onExit={handleExit}
        showSettings={true}
        onSettingsClick={() => setShowSettings(true)}
      />

      {/* Tab Navigation */}
      <div className="mb-4">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("scan-history")}
              className={tabButtonClass("scan-history")}
            >
              Scan History
            </button>
            <button
              onClick={() => setActiveTab("reports-operators")}
              className={tabButtonClass("reports-operators")}
            >
              Reports
            </button>
            <button
              onClick={() => setActiveTab("members")}
              className={tabButtonClass("members")}
            >
              Members
            </button>
            <button
              onClick={() => setActiveTab("shifts")}
              className={tabButtonClass("shifts")}
            >
              Shifts
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="h-[calc(100vh-180px)]">
        {activeTab === "scan-history" && (
          <div className="grid grid-cols-3 gap-4 h-full">
            {/* Scan History Panel */}
            <ScanHistoryPanel />

            {/* Valid Sets Panel */}
            <ValidSetsPanel />

            {/* Invalid Sets Panel */}
            <InvalidSetsPanel />
          </div>
        )}

        {activeTab === "reports-operators" && (
          <div className="h-full">
            <ActivityReportsPanel
              onSelectOperator={setSelectedOperator}
              onGenerateReport={handleGenerateReport}
              availableDates={availableDates}
              scanHistory={scanHistory} // NEW: Pass scanHistory
            />
          </div>
        )}

        {activeTab === "shifts" && (
          <div className="h-full">
            <ShiftsPanel />
          </div>
        )}

        {activeTab === "members" && (
          <div className="h-full">
            <MembersPanel operators={operators} />
          </div>
        )}
      </div>

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        cameraConfig={cameraConfig}
        onSaveCameraConfig={setCameraConfig}
      />
    </div>
  );
};
